import { API_URL } from '~/queries/constants';
import type { AuthenticatedApiFunctionOptions } from '~/lib/auth-api-wrapper';
import { withAuth } from '~/lib/auth-api-wrapper';

export enum Currency {
  USD = 'USD'
}

export enum SubscriptionTier {
  FREE = 'FREE',
  PRO = 'PRO',
  MAX = 'MAX',
}

const TIER_LABELS: Record<SubscriptionTier, string> = {
  [SubscriptionTier.FREE]: 'Free',
  [SubscriptionTier.PRO]: 'Pro',
  [SubscriptionTier.MAX]: 'Max',
};

/**
 * The tier as a reader should see it. The enum is SCREAMING_CASE because it is wire format, and
 * printing it verbatim is how "You've used all your FREE credits" reached production — a raw enum
 * dropped mid-sentence. Anything user-facing goes through here.
 */
export function planLabel(tier: SubscriptionTier): string {
  return TIER_LABELS[tier] ?? 'Free';
}

export interface PendingDowngrade {
  targetTier: SubscriptionTier;
  targetCredits: number;
  effectiveDate: string;
}

export interface BillingInfo {
  currentTier: SubscriptionTier;
  hasActiveSubscription: boolean;
  subscriptionExpiration?: string | number;
  nextBillingDate?: string | number;
  paymentMethodLast4?: string;
  paymentMethodBrand?: string;
  hasBillingPortalAccess: boolean;
  nextPaymentAmountSmallestUnit?: number;
  currency: Currency;
  pendingDowngrade?: PendingDowngrade;
}

export interface BillingPortalResponse {
  portalUrl: string;
}

export enum BillingInterval {
  MONTHLY,
  YEARLY,
}

export interface CheckoutSessionRequest {
  priceId: string;
}

export interface ChangePlanRequest {
  priceId: string;
}

export interface ChangePlanResponse {
  pendingDowngrade?: PendingDowngrade;
}

export interface CheckoutStatusResponse {
  status: 'complete' | 'open' | 'expired';
  customerId?: string;
  subscriptionId?: string;
}

export interface CheckoutSessionResponse {
  checkoutUrl: string;
}

export enum FeatureStatus {
  INCLUDED = 'INCLUDED',
  EXCLUDED = 'EXCLUDED',
  HIGHLIGHT = 'HIGHLIGHT',
}

export interface PricingPlanFeature {
  description: string;
  status: FeatureStatus;
}

export interface VolumeOption {
  credits: number;
  monthlyPriceId: string;
  monthlyAmount: number;
  yearlyPriceId: string;
  yearlyAmount: number;
}

export interface PricingPlan {
  tier: SubscriptionTier;
  name: string;
  description: string;
  isCurrentPlan: boolean;
  features: PricingPlanFeature[];
  volumes: VolumeOption[];
}

export interface PricingPlansResponse {
  plans: PricingPlan[];
  hasActiveSubscription: boolean;
  pendingDowngrade?: PendingDowngrade;
  currentPriceId?: string;
}

export interface ModelAccessEntry {
  model: string;
  accessible: boolean;
  requiredTier: SubscriptionTier;
}

export interface ModelAccessResponse {
  currentTier: SubscriptionTier;
  models: ModelAccessEntry[];
}

export interface CreditBalanceInfo {
  /** Total remaining = monthly allocation remaining + purchased top-up. */
  credits: number;
  /** Monthly allocation — the "used / X" meter denominator. */
  grantedCredits: number;
  /** Remaining purchased top-up credits (never expire). Monthly remaining = credits - topupCredits. */
  topupCredits: number;
}

/** A one-time credit top-up pack (paid users only). `amountCents` is in the smallest currency unit. */
export interface CreditPack {
  priceId: string;
  credits: number;
  amountCents: number;
  currency: string;
}

const fetchBillingInfo = async (
  options: AuthenticatedApiFunctionOptions
): Promise<BillingInfo> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/billing`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const createBillingPortalSession = async (
  options: AuthenticatedApiFunctionOptions
): Promise<BillingPortalResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/billing-portal-session`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const fetchPricingPlans = async (
  options: AuthenticatedApiFunctionOptions
): Promise<PricingPlansResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/pricing-plans`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const createCheckoutSession = async (
  options: AuthenticatedApiFunctionOptions,
  request: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const changePlan = async (
  options: AuthenticatedApiFunctionOptions,
  request: ChangePlanRequest
): Promise<ChangePlanResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/change-plan`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

// Plan changes for existing subscribers go through the Stripe Customer Portal (a hosted page that
// shows the prorated amount, lets the user enter a promo code, and collects payment) rather than a
// silent server-side charge. The backend returns the portal URL pre-targeted to the chosen plan.
export interface PlanChangePreview {
  changeType: 'UPGRADE' | 'DOWNGRADE' | 'FREE';
  amountDueNow: number; // minor units (cents): positive = charged now, negative = credited
  currency: string;
  cardBrand?: string | null;
  cardLast4?: string | null;
  effectiveDate?: string | null; // set for FREE (period end)
  targetCredits: number;
}

// Exact, pre-charge preview of a plan change (from Stripe's proration preview) for the in-app
// confirmation screen. Nothing is charged here — only on the subsequent change-plan call.
const previewPlanChange = async (
  options: AuthenticatedApiFunctionOptions,
  request: ChangePlanRequest
): Promise<PlanChangePreview> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');

  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/preview-plan-change`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const createPlanChangePortalSession = async (
  options: AuthenticatedApiFunctionOptions,
  request: ChangePlanRequest
): Promise<BillingPortalResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');

  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/change-plan-portal-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const cancelPendingDowngrade = async (
  options: AuthenticatedApiFunctionOptions
): Promise<void> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/pending-downgrade`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }
};

const fetchModelAccess = async (
  options: AuthenticatedApiFunctionOptions
): Promise<ModelAccessResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/model-access`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const fetchCheckoutStatus = async (
  options: AuthenticatedApiFunctionOptions,
  sessionId: string
): Promise<CheckoutStatusResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/checkout-status?sessionId=${encodeURIComponent(sessionId)}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const fetchCreditBalance = async (
  options: AuthenticatedApiFunctionOptions
): Promise<CreditBalanceInfo> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/credit-balance`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const fetchCreditPacks = async (
  options: AuthenticatedApiFunctionOptions
): Promise<CreditPack[]> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');

  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/credit-packs`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

const createTopupCheckoutSession = async (
  options: AuthenticatedApiFunctionOptions,
  request: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');

  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_URL}/subscription/topup-checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }

  return await response.json();
};

export const authenticatedFetchModelAccess = withAuth(fetchModelAccess);
export const authenticatedFetchBillingInfo = withAuth(fetchBillingInfo);
export const authenticatedCreateBillingPortalSession = withAuth(createBillingPortalSession);
export const authenticatedFetchPricingPlans = withAuth(fetchPricingPlans);
export const authenticatedCreateCheckoutSession = withAuth(createCheckoutSession);
export const authenticatedChangePlan = withAuth(changePlan);
export const authenticatedCreatePlanChangePortalSession = withAuth(createPlanChangePortalSession);
export const authenticatedPreviewPlanChange = withAuth(previewPlanChange);
export const authenticatedFetchCheckoutStatus = withAuth(fetchCheckoutStatus);
export const authenticatedCancelPendingDowngrade = withAuth(cancelPendingDowngrade);
export const authenticatedFetchCreditBalance = withAuth(fetchCreditBalance);
export const authenticatedFetchCreditPacks = withAuth(fetchCreditPacks);
export const authenticatedCreateTopupCheckoutSession = withAuth(createTopupCheckoutSession);
